import { HTTPError } from 'ky';

export type OAuthCallbackToastKey =
  | 'invalidClient'
  | 'invalidScope'
  | 'providerUnavailable'
  | 'unexpectedFailure';

const RFC_INVALID_CLIENT_CODES = new Set([
  'invalid_client',
  'invalid_grant',
  'unauthorized_client',
]);

const RFC_INVALID_SCOPE_CODES = new Set(['invalid_scope']);

const RFC_PROVIDER_UNAVAILABLE_CODES = new Set(['server_error', 'temporarily_unavailable']);

const BACKEND_INVALID_CLIENT_CODES = new Set([
  'OAUTH_INVALID_CLIENT',
  'OAUTH_INVALID_GRANT',
  'OAUTH_UNAUTHORIZED_CLIENT',
]);

const BACKEND_INVALID_SCOPE_CODES = new Set(['OAUTH_INVALID_SCOPE']);

export const mapAtlassianErrorParam = (errorParam: string): OAuthCallbackToastKey | null => {
  if (errorParam === 'access_denied') return null;
  if (RFC_INVALID_CLIENT_CODES.has(errorParam)) return 'invalidClient';
  if (RFC_INVALID_SCOPE_CODES.has(errorParam)) return 'invalidScope';
  if (RFC_PROVIDER_UNAVAILABLE_CODES.has(errorParam)) return 'providerUnavailable';
  return 'unexpectedFailure';
};

export const mapBackendError = async (error: unknown): Promise<OAuthCallbackToastKey> => {
  if (!(error instanceof HTTPError)) return 'unexpectedFailure';

  const status = error.response.status;
  if (status === 502 || status === 503) return 'providerUnavailable';

  if (status === 422) {
    const body = readErrorBody(error);
    const code = body?.code;
    if (code && BACKEND_INVALID_CLIENT_CODES.has(code)) return 'invalidClient';
    if (code && BACKEND_INVALID_SCOPE_CODES.has(code)) return 'invalidScope';
    return 'unexpectedFailure';
  }

  // Network-only auth failures (401) and everything else fall back to a generic message.
  return 'unexpectedFailure';
};

const readErrorBody = (error: HTTPError): { code?: string; message?: string } | null => {
  const body = error.data;
  if (body && typeof body === 'object' && 'code' in body) {
    return body as { code?: string; message?: string };
  }
  return null;
};
